import { useState, useEffect, useRef, useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useToast } from '@chakra-ui/react';
import useUser from 'hooks/useUser';
import { createFiltersFromQuery, createFiltersParamsUrl } from 'utils/helpers';
import Tracking from 'utils/tracking';

export default function useProductListing(
  page,
  data,
  info,
  pager,
  facets,
  sort,
  dataHook,
  apiCallToFetchData,
  trackingPageViewName = 'Product-Listing',
  title,
  isSearch
) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const toast = useToast();
  const { userData } = useUser();
  const [selectedSort, setSelectedSort] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreIsPressed, setLoadMoreIsPressed] = useState(false);
  const [wornImageActive, setWornImageActive] = useState(false);

  // Add refs to track scroll restoration
  const isLoadingMoreRef = useRef(false);
  const scrollPositionSaved = useRef(false);
  const previousDataLength = useRef(0);

  const { productCategoryData, mutate, isValidating } = dataHook(
    page.id || page.term,
    { data, info, pager, facets, sort },
    0,
    selectedSort,
    selectedFilters
  );

  // Disable browser's automatic scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Auto-refresh interval
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (window.pageYOffset < 100) {
  //       mutate();
  //     }
  //   }, 300000);

  //   return () => clearInterval(interval);
  // }, [mutate]);

  // Save scroll position on any navigation away from the page
  useEffect(() => {
    const saveScrollPosition = () => {
      if (!scrollPositionSaved.current && productCategoryData?.data?.length > 0) {
        const scrollKey = `scrollPos-${router.asPath}`;
        const scrollData = {
          position: window.pageYOffset || window.scrollY,
          timestamp: Date.now(),
          dataLength: productCategoryData?.data?.length || 0,
          pageHeight: document.documentElement.scrollHeight,
          viewportHeight: window.innerHeight,
        };
        sessionStorage.setItem(scrollKey, JSON.stringify(scrollData));
        scrollPositionSaved.current = true;
      }
    };

    // Save on route change
    const handleRouteChangeStart = (url) => {
      // Don't save for shallow routing (filters)
      if (!url.includes(router.asPath.split('?')[0].split('/').slice(0, -1).join('/'))) {
        saveScrollPosition();
      }
    };

    // Save on clicking any link that navigates away
    const handleClick = (e) => {
      const target = e.target.closest('a');
      if (target && target.href && !target.href.includes('#')) {
        const targetUrl = new URL(target.href, window.location.origin);
        const currentUrl = new URL(window.location.href);

        // If navigating to a different page, save scroll position
        if (targetUrl.pathname !== currentUrl.pathname) {
          saveScrollPosition();
        }
      }
    };

    // Save on browser back/forward
    const handlePopState = () => {
      saveScrollPosition();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    document.addEventListener('click', handleClick, true);
    window.addEventListener('popstate', handlePopState);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [router, productCategoryData?.data?.length]);

  // Reset scroll position saved flag when route changes
  useEffect(() => {
    scrollPositionSaved.current = false;
  }, [router.asPath]);

  // Restore scroll position after data loads
  useEffect(() => {
    // Skip if we're loading more items
    if (isLoadingMoreRef.current) return;

    // Skip if data hasn't loaded yet or is still validating
    if (!productCategoryData?.data?.length || isValidating) return;

    // Skip if this is a data update (not a navigation)
    if (previousDataLength.current === productCategoryData.data.length && previousDataLength.current > 0) return;

    previousDataLength.current = productCategoryData.data.length;

    const restoreScrollPosition = async () => {
      const scrollKey = `scrollPos-${router.asPath}`;
      const savedData = sessionStorage.getItem(scrollKey);

      if (!savedData) return;

      try {
        const { position, timestamp, dataLength } = JSON.parse(savedData);

        // Check if the saved position is recent (within 30 minutes)
        const isRecent = Date.now() - timestamp < 30 * 60 * 1000;

        if (!isRecent) {
          sessionStorage.removeItem(scrollKey);
          return;
        }

        // Wait for the next frame to ensure DOM is ready
        await new Promise((resolve) => requestAnimationFrame(resolve));

        // Check if enough products have loaded
        if (productCategoryData.data.length < dataLength) {
          // If we haven't loaded enough products yet, wait a bit and try again
          setTimeout(() => restoreScrollPosition(), 100);
          return;
        }

        // Get all product images
        const images = document.querySelectorAll('#productList img');
        let imagesToLoad = 0;
        let imagesLoaded = 0;

        // Count images that need to load
        images.forEach((img) => {
          if (!img.complete) {
            imagesToLoad++;
          }
        });

        if (imagesToLoad === 0) {
          // No images to wait for, restore immediately
          window.scrollTo(0, position);
          sessionStorage.removeItem(scrollKey);
          return;
        }

        // Function to check if we should restore scroll
        const checkAndRestore = () => {
          imagesLoaded++;
          // Restore after 50% of images are loaded or all are loaded
          if (imagesLoaded >= Math.ceil(imagesToLoad * 0.5) || imagesLoaded >= imagesToLoad) {
            window.scrollTo(0, position);
            sessionStorage.removeItem(scrollKey);
          }
        };

        // Set up image load listeners
        images.forEach((img) => {
          if (!img.complete) {
            const loadHandler = () => checkAndRestore();
            img.addEventListener('load', loadHandler, { once: true });
            img.addEventListener('error', loadHandler, { once: true });
          }
        });

        // Fallback: restore after 1.5 seconds regardless
        setTimeout(() => {
          const currentScroll = window.pageYOffset || window.scrollY;
          if (currentScroll < 100) {
            // Only restore if we're still at the top
            window.scrollTo(0, position);
            sessionStorage.removeItem(scrollKey);
          }
        }, 1500);
      } catch (error) {
        console.error('Error restoring scroll position:', error);
        sessionStorage.removeItem(scrollKey);
      }
    };

    restoreScrollPosition();
  }, [router.asPath, productCategoryData?.data?.length, isValidating]);

  const getbatchedLists = (data) => {
    const batchedArray = data?.reduce((accumulator, currentValue, currentIndex) => {
      const arrayIndex = Math.floor(currentIndex / 12);
      if (accumulator[arrayIndex]) {
        accumulator[arrayIndex].push(currentValue);
      } else {
        accumulator[arrayIndex] = [currentValue];
      }
      return accumulator;
    }, []);

    productCategoryData?.data?.length > 0 &&
      batchedArray?.map((data, index) => {
        const previousBatchesTotalElements = batchedArray.slice(0, index).reduce((acc, arr) => acc + arr.length, 0);
        Tracking.viewItemList(
          data,
          productCategoryData?.pager?.totalResults,
          isSearch,
          false,
          productCategoryData?.info?.name,
          previousBatchesTotalElements
        );
      });

    return batchedArray;
  };

  const getLatestItemsLists = (data) => {
    const newArray = data.slice(-24);

    const batchedArray = newArray?.reduce((accumulator, currentValue, currentIndex) => {
      const arrayIndex = Math.floor(currentIndex / 12);
      if (accumulator[arrayIndex]) {
        accumulator[arrayIndex].push(currentValue);
      } else {
        accumulator[arrayIndex] = [currentValue];
      }
      return accumulator;
    }, []);

    productCategoryData?.data?.length > 0 &&
      batchedArray?.map((data, index) => {
        const previousBatchesTotalElements = batchedArray.slice(0, index).reduce((acc, arr) => acc + arr.length, 0);
        Tracking.viewItemList(
          data,
          productCategoryData?.pager?.totalResults,
          isSearch,
          false,
          productCategoryData?.info?.name,
          previousBatchesTotalElements
        );
      });

    return batchedArray;
  };

  useEffect(() => {
    const selectedFiltersPath = createFiltersParamsUrl(selectedFilters);
    let pageUrl = page?.term ? `${page?.path}/${page.term}` : `${page?.path}`;

    if (selectedFiltersPath && selectedFiltersPath.length > 0) {
      router.push(`${pageUrl}/${selectedFiltersPath}`, undefined, {
        shallow: true,
      });
    } else {
      router.push(`${pageUrl}`, undefined, {
        shallow: true,
      });
    }
  }, [JSON.stringify(selectedFilters), router.locale]);

  useEffect(() => {
    let removeFilters = localStorage.getItem('removeFilters');

    if (removeFilters) {
      setSelectedFilters(null);
      localStorage.removeItem('removeFilters');
    }
  }, [router.locale]);

  useEffect(() => {
    if (page.query) {
      setSelectedFilters(createFiltersFromQuery(page.query, productCategoryData.facets));
      setSelectedSort(null);
      setWornImageActive(false);
      const selectedFiltersPath = createFiltersParamsUrl(
        createFiltersFromQuery(page.query, productCategoryData.facets)
      );
      let pageUrl = page?.term ? `${page?.path}/${page.term}` : `${page?.path}`;

      Tracking.pageView(
        trackingPageViewName,
        router.locale,
        `${pageUrl}/${selectedFiltersPath}`,
        page?.query,
        page?.term,
        userData?.id,
        info?.name || title,
        { full_URL: `${window.location.protocol}${window.location.host}${pageUrl}/${selectedFiltersPath}` },
        { categoryId: page?.id }
      );
    } else {
      let pageUrl = page?.term ? `${page?.path}/${page.term}` : `${page?.path}`;
      Tracking.pageView(
        trackingPageViewName,
        router.locale,
        `${pageUrl}`,
        page?.query,
        page?.term,
        userData?.id,
        info?.name || title,
        undefined,
        { categoryId: page?.id }
      );
      setSelectedFilters(null);
      setSelectedSort(null);
      setWornImageActive(false);
    }
  }, [page.id, page?.term]);

  const dataAsString = JSON.stringify(productCategoryData?.data);

  const handleSelectFilter = (facet, filter) => {
    let newFilters = { ...selectedFilters };
    if (newFilters[facet]) {
      if (newFilters[facet].find((i) => i.filter_transliterated === filter.filter_transliterated)) {
        newFilters[facet] = newFilters[facet].filter((i) => i.filter_transliterated !== filter.filter_transliterated);
      } else {
        newFilters[facet].push(filter);
      }
    } else {
      newFilters[facet] = [filter];
    }

    setSelectedFilters(newFilters);

    const selectedFiltersPath = createFiltersParamsUrl(newFilters);
    let pageUrl = page?.term ? `${page?.path}/${page.term}` : `${page?.path}`;

    Tracking.pageView(
      trackingPageViewName,
      router.locale,
      `${pageUrl}/${selectedFiltersPath}`,
      selectedFiltersPath,
      page?.term,
      userData?.id,
      info?.name,
      { full_URL: `${window.location.protocol}${window.location.host}${pageUrl}/${selectedFiltersPath}` },
      { categoryId: page?.id }
    );
  };

  const handleRemoveFilter = (filter) => {
    let newFilters = { ...selectedFilters };
    let facet = filter.facet;
    newFilters[facet] = newFilters[facet].filter((i) => i.filter_transliterated !== filter.filter_transliterated);

    if (newFilters[facet].length === 0) {
      delete newFilters[facet];
    }

    setSelectedFilters(newFilters);
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    setLoadMoreIsPressed(true);
    isLoadingMoreRef.current = true;

    // Save current scroll position before loading more
    const currentScrollPosition = window.scrollY;
    const newPageNumber = productCategoryData.pager.currentpage + 1;

    apiCallToFetchData(router.locale, isSearch ? page.term : page.id, newPageNumber, selectedSort, selectedFilters)
      .then((res) => {
        mutate(
          {
            ...productCategoryData,
            data: productCategoryData.data.concat(res.data.data),
            pager: res.data.pager,
          },
          false
        );

        // Maintain scroll position after mutation
        requestAnimationFrame(() => {
          window.scrollTo(0, currentScrollPosition);
        });
      })
      .catch((err) => {
        console.log(err);
        toast({
          title: t('error'),
          description: t('errorLoadingMoreProducts'),
          position: 'bottom-right',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoadingMore(false);
        // Reset the flag after a delay
        setTimeout(() => {
          isLoadingMoreRef.current = false;
        }, 100);
      });
  };

  const handleWornSwitch = () => {
    setWornImageActive(!wornImageActive);
  };

  useEffect(() => {
    !loadMoreIsPressed ? getbatchedLists(productCategoryData?.data) : getLatestItemsLists(productCategoryData?.data);
  }, [dataAsString, productCategoryData?.pager?.totalResults]);

  // Debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Product listing state:', {
        path: router.asPath,
        dataLength: productCategoryData?.data?.length,
        isValidating,
        scrollY: window.scrollY,
      });
    }
  }, [router.asPath, productCategoryData?.data?.length, isValidating]);

  return {
    handleWornSwitch,
    handleLoadMore,
    handleRemoveFilter,
    handleSelectFilter,
    selectedSort,
    selectedFilters,
    loadingMore,
    wornImageActive,
    productCategoryData,
    isValidating,
    setSelectedSort,
    setSelectedFilters,
  };
}
